import { useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { PendingTask, PendingTaskType, UnreadTask } from '../types/member.type';
import { getPendingTasks } from '../api/task';
import { setPendingTasks, setUnreadTasks } from '../store/memberSlice';

export function usePendingTask(taskType: PendingTaskType): PendingTask | null {
    const pendingTasks = useSelector((state: RootState) => state.member.pendingTasks);

    if (!pendingTasks?.length) return null;

    return pendingTasks.find(task => task.taskType === taskType) ?? null;
}

export function usePendingTaskPoller(onStop?: () => void) {
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const onStopRef = useRef(onStop);
    const isRestartingRef = useRef(false);

    onStopRef.current = onStop;

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (!isRestartingRef.current) {
            onStopRef.current?.();
        }
    }, []);

    const startPolling = useCallback(() => {
        isRestartingRef.current = true;
        stopPolling();
        isRestartingRef.current = false;

        const poll = async () => {
            try {
                const result = await getPendingTasks();

                if (!result) {
                    stopPolling();
                    const current = store.getState().member.pendingTasks;
                    if (current.length > 0) {
                        dispatch(setPendingTasks([]));
                    }
                    return;
                }

                const { pending, unread } = result;

                // unread: only add new items, never auto-clear
                if (unread && unread.length > 0) {
                    dispatch(setUnreadTasks(unread));
                }

                // pending: stop polling & clear when empty
                if (!pending || pending.length === 0) {
                    stopPolling();
                    const current = store.getState().member.pendingTasks;
                    if (current.length > 0) {
                        dispatch(setPendingTasks([]));
                    }
                    return;
                }

                const current = store.getState().member.pendingTasks;
                const currentStr = JSON.stringify(current);
                const newStr = JSON.stringify(pending);

                if (currentStr !== newStr) {
                    dispatch(setPendingTasks(pending));
                }
            } catch (e) {
                stopPolling();
            }
        };

         poll().then();
        intervalRef.current = setInterval(poll, 2000);
    }, [dispatch, store, stopPolling]);

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            onStopRef.current?.();
        };
    }, []);

    return { startPolling, stopPolling };
}
